import { useCallback, useId, useSyncExternalStore } from "react";

// Shared state so moving between hovercards skips the open delay
// and only one hovercard is open at a time
let anyOpen = false;
let activeId: string | null = null;
let graceTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function getSnapshot() {
  return { warmed: anyOpen, activeId };
}

let cachedSnapshot = getSnapshot();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notify() {
  cachedSnapshot = getSnapshot();
  listeners.forEach((cb) => cb());
}

function openHovercard(id: string) {
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  anyOpen = true;
  activeId = id;
  notify();
}

function closeHovercard(id: string) {
  if (activeId !== id) {
    return;
  }
  activeId = null;
  graceTimer = setTimeout(() => {
    anyOpen = false;
    notify();
  }, 300);
  notify();
}

interface SharedHovercardState {
  isOpen: boolean;
  openDelay: number;
  handleOpenChange: (open: boolean) => void;
}

export function useSharedHovercard({
  defaultOpenDelay = 500,
}: {
  defaultOpenDelay?: number;
} = {}): SharedHovercardState {
  const id = useId();
  const snapshot = useSyncExternalStore(subscribe, () => cachedSnapshot);
  const isOpen = snapshot.activeId === id;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openHovercard(id);
      } else {
        closeHovercard(id);
      }
    },
    [id],
  );

  return {
    isOpen,
    openDelay: snapshot.warmed ? 0 : defaultOpenDelay,
    handleOpenChange,
  };
}
